import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Phone, MessageCircle, Mail, ChevronRight } from "lucide-react";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { COMPANY } from "@/lib/company";
import { CATALOG_GROUPS } from "@/lib/catalog";
import { SERVICE_PRICE_FROM } from "@/content/service-pillars/price-from.gen";
import type { ServicePillarMeta } from "@/content/service-pillars/meta.gen";

export const Route = createFileRoute("/catalog")({
  loader: async (): Promise<{ items: ServicePillarMeta[] }> => {
    const { SERVICE_PILLAR_META } = await import("@/content/service-pillars/meta");
    return { items: SERVICE_PILLAR_META };
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `แคตตาล็อกบริการทั้งหมด ${items.length} รายการ | Thai Notary ${config.nameEn}`;
    const description = `ไดเรกทอรีบริการทั้งหมด ${items.length} รายการของ Thai Notary — Notary Public, แปลและรับรองคำแปล, นิติกรณ์กรมการกงสุลและสถานทูต, ตรวจประวัติอาชญากรรม, วีซ่าและจดทะเบียนสมรส ค้นหาและกรองตามหมวดได้ในหน้าเดียว`;
    const base = pageHeadForProvince({
      title,
      description,
      path: "/catalog",
      alternates: [{ lang: "en", path: "/en/catalog" }],
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/catalog")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "แคตตาล็อกบริการ", url: `${site}/catalog` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${site}/catalog#collection`,
            url: `${site}/catalog`,
            name: title,
            description,
            inLanguage: "th-TH",
            isPartOf: { "@id": `${site}/#website` },
            about: { "@id": `${site}/#organization` },
            mainEntity: {
              "@type": "ItemList",
              name: "แคตตาล็อกบริการ Thai Notary",
              numberOfItems: items.length,
              itemListElement: items.map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: s.h1,
                url: `${site}/services/${s.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            "@id": `${site}/catalog#offercatalog`,
            name: `บริการทั้งหมดของ ${COMPANY.brandName}`,
            url: `${site}/catalog`,
            provider: { "@id": `${site}/#organization` },
            itemListElement: CATALOG_GROUPS.map((g) => ({
              "@type": "OfferCatalog",
              name: g.labelTh,
              description: g.descTh,
              itemListElement: items
                .filter((s) => s.category === g.id)
                .map((s) => {
                  const from = SERVICE_PRICE_FROM[s.slug];
                  return {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: s.h1,
                      description: s.metaDesc,
                      url: `${site}/services/${s.slug}`,
                      serviceType: g.labelEn,
                      provider: { "@id": `${site}/#organization` },
                    },
                    availability: "https://schema.org/InStock",
                    url: `${site}/services/${s.slug}`,
                    ...(from
                      ? {
                          price: from,
                          priceCurrency: "THB",
                          priceSpecification: {
                            "@type": "PriceSpecification",
                            price: from,
                            priceCurrency: "THB",
                            valueAddedTaxIncluded: false,
                            description: "ราคาเริ่มต้น",
                          },
                        }
                      : {}),
                  };
                }),
            })),
          }),
        },
      ],
    };
  },
  component: CatalogPage,
});

function CatalogPage() {
  const { items } = Route.useLoaderData() as { items: ServicePillarMeta[] };
  const { config } = getActiveProvince();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("all");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((s) => {
      if (cat !== "all" && s.category !== cat) return false;
      if (!needle) return true;
      return (
        s.h1.toLowerCase().includes(needle) ||
        s.metaTitle.toLowerCase().includes(needle) ||
        s.metaDesc.toLowerCase().includes(needle) ||
        s.slug.toLowerCase().includes(needle)
      );
    });
  }, [items, q, cat]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of items) map[s.category] = (map[s.category] ?? 0) + 1;
    return map;
  }, [items]);

  return (
    <ProvincePageShell
      eyebrow="Service Catalog"
      title={`แคตตาล็อกบริการทั้งหมด ${items.length} รายการ`}
      subtitle={`ไดเรกทอรีบริการครบทุกหมวดของ ${COMPANY.brandName} ใน${config.nameTh} — ค้นหาด้วยคำค้น กรองตามหมวด แล้วกดเข้าไปอ่านรายละเอียดเอกสาร ขั้นตอน และระยะเวลาของแต่ละบริการ ค่าบริการขอให้สอบถามเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมล เพราะขึ้นกับชนิดเอกสารและหน่วยงานปลายทาง`}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "แคตตาล็อกบริการ" }]}
    >
      <div className="mx-auto max-w-7xl space-y-10 px-4 py-14">
        <section aria-label="ค้นหาบริการ" className="rounded-2xl border border-border bg-card p-5">
          <label htmlFor="catalog-search" className="mb-2 block text-sm font-bold text-primary">
            ค้นหาบริการ
          </label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="catalog-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="เช่น notary, NAATI, กงสุล, วีซ่า, สมรส, ตรวจประวัติ"
              className="w-full rounded-xl border border-border bg-background py-3 pl-10 pr-3 text-sm outline-none focus:border-gold"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCat("all")}
              aria-pressed={cat === "all"}
              className={`rounded-md border px-3 py-1.5 text-xs font-bold ${cat === "all" ? "border-gold text-gold" : "border-border bg-secondary/60 text-primary"}`}
            >
              ทุกหมวด ({items.length})
            </button>
            {CATALOG_GROUPS.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setCat(g.id)}
                aria-pressed={cat === g.id}
                className={`rounded-md border px-3 py-1.5 text-xs font-bold ${cat === g.id ? "border-gold text-gold" : "border-border bg-secondary/60 text-primary"}`}
              >
                {g.labelTh} ({counts[g.id] ?? 0})
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground" role="status">
            แสดง {filtered.length} จาก {items.length} บริการ
          </p>
        </section>

        {CATALOG_GROUPS.filter((g) => cat === "all" || cat === g.id).map((g) => {
          const rows = filtered.filter((s) => s.category === g.id);
          if (rows.length === 0) return null;
          return (
            <section key={g.id} id={`cat-${g.id}`} aria-labelledby={`h-${g.id}`}>
              <h2 id={`h-${g.id}`} className="text-xl font-black text-primary md:text-2xl">
                {g.labelTh}{" "}
                <span className="text-sm font-bold text-muted-foreground">({rows.length} รายการ)</span>
              </h2>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground">{g.descTh}</p>
              <ul className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {rows.map((s) => (
                  <li key={s.slug} className="rounded-2xl border border-border bg-card p-5">
                    <h3 className="text-base font-bold leading-snug text-primary">
                      <Link
                        to="/services/$service"
                        params={{ service: s.slug }}
                        className="hover:text-gold"
                      >
                        {s.metaTitle.split("|")[0].trim()}
                      </Link>
                    </h3>
                    <p className="mt-2 line-clamp-4 text-sm text-muted-foreground">{s.metaDesc}</p>
                    <Link
                      to="/services/$service"
                      params={{ service: s.slug }}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-gold"
                    >
                      รายละเอียดและเอกสารที่ต้องใช้ <ChevronRight className="h-3 w-3" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <section aria-labelledby="catalog-quote" className="rounded-2xl border border-gold/40 bg-gold/5 p-6">
          <h2 id="catalog-quote" className="text-lg font-black text-primary">
            ขอใบเสนอราคาและตรวจเอกสารก่อนเริ่มงาน
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            ค่าบริการแต่ละงานขึ้นกับชนิดเอกสาร จำนวนหน้า ภาษา หน่วยงานปลายทาง และความเร่งด่วน จึงไม่ระบุราคาไว้ในหน้านี้
            ส่งภาพเอกสารให้เจ้าหน้าที่ตรวจก่อนได้ เราจะแจ้งเอกสารที่ต้องเตรียม ขั้นตอน ระยะเวลา และค่าใช้จ่ายทั้งหมดเป็นลายลักษณ์อักษรก่อนเริ่มงาน
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href={`tel:${COMPANY.phoneIntl}`} className="inline-flex items-center gap-2 rounded-xl border-2 border-gold px-4 py-2.5 text-sm font-bold text-gold">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="inline-flex items-center gap-2 rounded-xl border-2 border-gold px-4 py-2.5 text-sm font-bold text-gold">
              <MessageCircle className="h-4 w-4" /> LINE {COMPANY.lineId}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 rounded-xl border-2 border-gold px-4 py-2.5 text-sm font-bold text-gold">
              <Mail className="h-4 w-4" /> {COMPANY.email}
            </a>
          </div>
        </section>
      </div>
    </ProvincePageShell>
  );
}
